<template>
    <v-row class="ga-4">
        <v-col cols="12" sm="auto">
            <v-tooltip location="top">
                <template #activator="{ props }">
                    <v-icon
                        v-bind="props"
                        icon="mdi-refresh"
                        class="cursor-pointer ma-6"
                        @click="resetInputs"
                        opacity="0.6"
                    />
                </template>
                <span>Reset to default</span>
            </v-tooltip>
        </v-col>
        <!-- Sort By -->
        <v-col cols="12" sm="6" md="4" lg="2">
            <v-select
                label="Sort By"
                :items="sortByItems"
                item-title="text"
                item-value="value"
                v-model="type"
                variant="underlined"
                color="typography2"
            />
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="2">
            <v-autocomplete
                label="Period"
                :items="timePeriods"
                item-title="title"
                item-value="value"
                v-model="selectedTimePeriod"
                variant="underlined"
                color="typography2"
            />
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="2">
            <v-autocomplete
                label="User Regions"
                :items="regions"
                multiple
                v-model="selectedRegions"
                variant="underlined"
                color="typography2"
            />
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="2">
            <v-autocomplete
                label="User Disciplines"
                :items="disciplines"
                multiple
                v-model="selectedDisciplines"
                variant="underlined"
                color="typography2"
            ></v-autocomplete>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="2" class="d-flex align-start">
            <v-select
                label="Clients"
                :items="clientItems"
                v-model="selectedClientGroup"
                variant="underlined"
                color="typography2"
            ></v-select>
            <v-tooltip location="top" color="grey-darken-1">
                <template v-slot:activator="{ props }">
                    <v-icon v-bind="props" size="x-small" color="grey-darken-1">
                        mdi-information
                    </v-icon>
                </template>
                A client is how a user interacts with ArupCompute. The core
                clients are: this webpage, Excel and Grasshopper. Other clients
                include the python client, or via the API, and thus are more
                likely to be used in automated routines.
            </v-tooltip>
        </v-col>
    </v-row>
</template>
<script setup>
import { ref, watch } from 'vue'

const props = defineProps({ inputsResetting: Boolean })
const emit = defineEmits(['runGetResults', 'update:inputsResetting'])

const sortByItems = [
    { text: 'Projects', value: 3 },
    { text: 'Users', value: 1 },
    { text: 'Calls', value: 2 },
]
const type = ref(1)
const clientItems = ['All', 'Core']
const coreClients = ['webui', 'excel', 'grasshopper']
const allClients = [
    ...coreClients,
    'arupcompute-connect-dotnet',
    'arupcompute-local-proxy',
    'arupcomputepy',
    'calcbuilder',
    'dc2_py_stubs',
    'designcheck2',
    'ovature',
    'postman',
]
const disciplines = [
    'Acoustics',
    'Administration',
    'Advanced Digital Engineering',
    'Airport Planning',
    'Architecture',
    'BIM',
    'Bridge & Civil Structures',
    'Building Physics',
    'Business Support',
    'CAD',
    'Civil Engineering',
    'Civil Structures',
    'Commercial Leadership and Management',
    'Data',
    'Design Management and Strategy',
    'Design Management',
    'Digital Advisory',
    'Digital Services',
    'Digital Technology',
    'Economic Planning',
    'Electrical Engineering',
    'Energy',
    'Environmental Consulting',
    'Facade Engineering',
    'Fire',
    'Geology',
    'Geotechnics',
    'Ground Engineering',
    'Highways',
    'Lighting',
    'Management Consulting',
    'Maritime(inc Ports)',
    'Mechanical Engineering',
    'Process Engineering',
    'Product Management',
    'Programme and Project Management',
    'Public Health and Plumbing Engineering',
    'Quality Management',
    'Rail System Engineering',
    'Rail',
    'Security',
    'Software Development',
    'Structural Engineering',
    'Sustainability',
    'Transport Planning',
    'Tunnelling',
    'Water',
]
const timePeriods = [
    { title: 'All time', value: 1 },
    { title: 'Financial year to date', value: 3 },
    { title: 'Current month', value: 4 },
]
const regions = [
    'Americas Region',
    'Australasia Region',
    'Corporate Services',
    'East Asia Region',
    'Europe',
    'Group Board',
    'UKIMEA Region',
]
const selectedDisciplines = ref([])
const selectedRegions = ref([])
const selectedClientGroup = ref('Core')
const selectedClients = ref([...coreClients])
const take = ref(10)
const selectedTimePeriod = ref(1)

watch(selectedClientGroup, () => {
    if (selectedClientGroup.value === 'All')
        selectedClients.value = [...allClients]
    else if (selectedClientGroup.value === 'Core')
        selectedClients.value = [...coreClients]
    else selectedClients.value = []
})
watch(type, () => runGetResults())
watch(
    selectedClients,
    (newVal, oldVal) => {
        if (
            (newVal.length === 0 && oldVal.length === 0) ||
            props.inputsResetting
        )
            return
        runGetResults()
    },
    { deep: true }
)
watch(
    selectedRegions,
    (newVal, oldVal) => {
        if (
            (newVal.length === 0 && oldVal.length === 0) ||
            props.inputsResetting
        )
            return
        runGetResults()
    },
    { deep: true }
)
watch(
    selectedDisciplines,
    (newVal, oldVal) => {
        if (
            (newVal.length === 0 && oldVal.length === 0) ||
            props.inputsResetting
        )
            return
        runGetResults()
    },
    { deep: true }
)
watch(selectedTimePeriod, () => {
    if (props.inputsResetting) return
    runGetResults()
})

function runGetResults() {
    emit('runGetResults', {
        type: type.value,
        selectedDisciplines: selectedDisciplines.value,
        selectedRegions: selectedRegions.value,
        take: take.value,
        selectedTimePeriod: selectedTimePeriod.value,
        selectedClients: selectedClients.value,
    })
}

function resetInputs() {
    emit('update:inputsResetting', true)
    selectedClientGroup.value = 'All'
    selectedClients.value = [...allClients]
    selectedDisciplines.value = []
    selectedRegions.value = []
    selectedTimePeriod.value = 1
    type.value = 1
    runGetResults()
}
</script>
